import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * RootRecast — "break the word" opener for the broken-plural lesson.
 *
 * Each row holds a base word and a recast button. Tapping it pours the word
 * into a new shape — redrawn in gold, with the shape it was poured into
 * (فُعُول, أَفْعَال…) named beside it — and plays the result. Tapping again
 * puts the base back for comparison. Completes when every row has been
 * recast once.
 *
 * The row's root tiles sit under the word and never change: they are the
 * whole point of the lesson, so they are rendered as isolated letters in
 * their own boxes (never as spans slicing a shaped word) and stay put while
 * the word above them is rebuilt.
 *
 * Lesson 27 recasts a singular into its broken plural and names its fields
 * `singular_ar` and `plural_ar`; lesson 30 recasts an adjective into its
 * أَفْعَل comparative, which is the same mechanic over a different pair of
 * words, so a row may equally use the neutral `base_ar` and `recast_ar`.
 */
const baseAr = (item) => item.base_ar || item.singular_ar;
const recastAr = (item) => item.recast_ar || item.plural_ar;
const baseEn = (item) => item.base_en || item.singular_en;
const recastEn = (item) => item.recast_en || item.plural_en;

function RootTiles({ letters, label, active }) {
  return (
    <div className="flex items-center gap-2 mt-2">
      <span className="flex gap-1.5" dir="rtl">
        {letters.map((letter, i) => (
          <span
            key={i}
            lang="ar"
            className="font-arabic inline-flex items-center justify-center w-8 h-9 rounded-lg text-2xl leading-none pt-1"
            style={{
              background: active ? "var(--gold-bg)" : "var(--ink)",
              border: `1px solid ${active ? "var(--gold-dim)" : "var(--panel-line)"}`,
              color: active ? "var(--gold-bright)" : "var(--parchment-dim)",
            }}
          >
            {letter}
          </span>
        ))}
      </span>
      <span className="text-xs" style={{ color: "var(--parchment-dim)" }}>{label}</span>
    </div>
  );
}

export default function RootRecast({ data, onComplete, audio }) {
  const [broken, setBroken] = useState(() => new Set());
  const [everBroken, setEverBroken] = useState(() => new Set());

  const done = everBroken.size >= data.items.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleBreak = (item) => {
    const isBroken = broken.has(item.id);
    setBroken((prev) => {
      const next = new Set(prev);
      if (isBroken) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
    if (!isBroken) {
      setEverBroken((prev) => new Set(prev).add(item.id));
      if (audio) audio.play(recastAr(item));
    }
  };

  return (
    <ActivityCard>
      <div className="flex justify-end mb-3">
        <ProgressPill>{everBroken.size}/{data.items.length} broken</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {data.items.map((item) => {
          const isBroken = broken.has(item.id);
          const seen = everBroken.has(item.id);
          const word = isBroken ? recastAr(item) : baseAr(item);
          return (
            <div
              key={item.id}
              className="px-4 pt-5 pb-3.5 rounded-xl"
              style={{
                background: isBroken ? "var(--gold-bg)" : "var(--ink)",
                border: seen ? "1px solid var(--gold-dim)" : "1px solid var(--panel-line)",
              }}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleBreak(item)}
                  aria-pressed={isBroken}
                  title={isBroken ? data.back_label : data.shift_label_en}
                  className="shrink-0 self-center rounded-lg px-3 py-2 transition-all hover:brightness-110"
                  style={
                    isBroken
                      ? { background: "var(--gold)", border: "1px solid var(--gold)", color: "var(--ink)" }
                      : { background: "var(--ink)", border: "1px solid var(--gold-dim)", color: "var(--gold-bright)" }
                  }
                >
                  <span lang="ar" dir="rtl" className="font-arabic text-2xl leading-[1.4] block">
                    {data.shift_label}
                  </span>
                  <span className="text-[10px] block" style={isBroken ? undefined : { color: "var(--parchment-dim)" }}>
                    {isBroken ? data.back_label : data.shift_label_en}
                  </span>
                </button>

                <div className="flex-1 min-w-0">
                  {/* Amiri stacks its marks above the em box, so the word gets
                      its own top padding rather than trusting line-height. */}
                  <span className="flex items-center gap-2.5 flex-wrap pt-1">
                    <span
                      dir="rtl"
                      lang="ar"
                      className="font-arabic text-4xl leading-[1.9]"
                      style={{ color: isBroken ? "var(--gold-bright)" : "var(--parchment)" }}
                    >
                      {word}
                    </span>
                    {isBroken && item.pattern_ar && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-2xl px-2.5 pt-1 pb-0.5 text-[0.72rem] font-semibold"
                        style={{ background: "var(--violet-bg)", color: "var(--tag-violet)" }}
                      >
                        {data.pattern_label || "shape"}
                        <span dir="rtl" lang="ar" className="font-arabic text-lg leading-[1.5]">{item.pattern_ar}</span>
                      </span>
                    )}
                  </span>
                  <p className="text-sm mt-0.5 mb-0" style={{ color: "var(--parchment-dim)" }}>
                    {isBroken ? recastEn(item) : baseEn(item)}
                  </p>

                  <RootTiles letters={item.root} label={data.root_label} active={isBroken} />

                  {isBroken && item.note && (
                    <p className="text-sm mt-2 mb-0 px-3 py-1.5 rounded-lg inline-block" style={{ background: "var(--violet-bg)", color: "var(--parchment)" }}>
                      <MixedText text={item.note} />
                    </p>
                  )}
                </div>

                {audio && audio.audioAllowed && (
                  <PlayButton text={word} playingText={audio.playingText} onPlay={audio.play} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">{data.icon || "🧩"}</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.done_message} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
